import React, { useState, useMemo, useEffect } from 'react';
import SquarePaymentForm from './SquarePaymentForm';
import { apiService } from '@/services/api';
import { antiAgingPlans } from '@/data/antiAgingData';
import { womenSexualHealthPlans } from '@/data/womenSexualHealthData';
import { mensSexualHealthPlans } from '@/data/mensSexualHealthData';
import { brandedWeightlossPlans } from '@/data/brandedWeightlossData';
import { RecommendationGate, BenefitList, TrustBanner, ProviderReviewNotice } from './checkoutUi';

// Anti-aging plans live as a hardcoded module export (the intake uses a
// separate AntiAgingCheckoutStep component that reads antiAgingPlans
// directly). The backend's /client-plan/active-list-v2 only seeds GLP-1
// + sermorelin/tesamorelin rows — anti-aging products (NAD+, MIC+B12,
// Glutathione, B12, Metformin) are FE-side only. Flatten the nested
// category structure so the follow-up CheckoutStep can render them
// alongside the GLP-1 / metabolic plan shape.
const flattenAntiAgingPlans = () =>
  antiAgingPlans.flatMap((category) =>
    (category.plans || []).map((plan) => ({
      ...plan,
      plan_glp_medicine: category.category,
    }))
  );

// Generic flatten for nested category→plans exports (WSH / MSH follow-ups).
const flattenCategoryPlans = (categories) =>
  (categories || []).flatMap((category) =>
    (category.plans || []).map((plan) => ({
      ...plan,
      plan_glp_medicine: category.category,
    }))
  );

const CheckoutStep = ({ onComplete, isProcessing, answers,setLoading,clearSavedProgress, primaryGoal,
              currentStep,
              timestamp, email, goalType, userData, shipTo, paymentHeld,
              modalityVisitType, modalityState }) => {
  // §staging-signoff: follow-up sign-off bypass — when ?belugaEnv=staging is in
  // the URL on a trt or women's-sexual-health follow-up, replace the Square
  // widget with a one-click submit of the STAGING_TEST_BYPASS sentinel. Gated on
  // goalType so only the BE's allowed follow-up visit types can bypass; the prod
  // BE quad-gate (belugaEnv:staging + visitType ∈ allowed-set + kill-switch +
  // sentinel) keeps real patients unaffected. Mirrors TrtCheckoutStep.
  const stagingTest =
    typeof window !== 'undefined' &&
    (goalType === 'trt' ||
      goalType === 'womenSexualHealth' ||
      goalType === 'mensSexualHealth' ||
      goalType === 'brandedWeightloss') &&
    new URLSearchParams(window.location.search).get('belugaEnv') === 'staging';

  // Label the staging-bypass notice with the actual follow-up visitType so it
  // matches what gets submitted (was hardcoded to "trtFollowup").
  const followupVisitTypeLabel =
    goalType === 'womenSexualHealth'
      ? 'womenSexualHealthFollowup'
      : goalType === 'mensSexualHealth'
      ? 'mensSexualHealthFollowup'
      : goalType === 'trt'
      ? 'trtFollowup'
      : goalType === 'brandedWeightloss'
      ? 'weightlossfollowup (branded)'
      : `${goalType}Followup`;

  // GLP-1 weightloss flow: medication-selection drives Tirzepatide vs.
  // Semaglutide filtering. Metabolic + anti-aging followups have no
  // medication-selection slide (per Beluga source-of-truth) — derive the
  // medication name from the patient's existing patientPreference instead.
  const isMetabolic = goalType === 'metabolic';
  const isAntiAging = goalType === 'antiAging';
  const isWomenSexualHealth = goalType === 'womenSexualHealth';
  const isMensSexualHealth = goalType === 'mensSexualHealth';
  const isBrandedWeightloss = goalType === 'brandedWeightloss';
  const selectedMedication = answers?.['medication-selection'] || 'semaglutide';
  const metabolicMed = (userData?.patientPreference?.[0]?.name || '').toLowerCase();
  const [activePlans, setActivePlans] = useState([]);


  useEffect(()=>{
      setLoading(true)
      apiService.getActivePlanList().then((res)=>{
              setLoading(false)
      console.log('Active Plans:', res.List);
      if (isAntiAging) {
        // Anti-aging plans (NAD+, MIC+B12, Vitamin B12, Glutathione, Metformin)
        // are hardcoded in antiAgingData.js — backend's active-list-v2 doesn't
        // seed them. Flatten + filter on the patient's existing
        // patientPreference; fall back to all anti-aging plans if unparseable.
        const allAntiAging = flattenAntiAgingPlans();
        const antiAgingTokens = ['nad', 'glutathione', 'metformin', 'mic+b12', 'mic-b12', 'b12', 'vitamin-b12'];
        const matchToken = antiAgingTokens.find((tok) => metabolicMed.includes(tok));
        const filteredPlans = allAntiAging.filter((plan) => {
          const slug = (plan.plan_slug || '').toLowerCase();
          const name = (plan.plan_name || '').toLowerCase();
          const med = (plan.plan_glp_medicine || '').toLowerCase();
          if (matchToken) {
            return slug.includes(matchToken) || name.includes(matchToken) || med.includes(matchToken);
          }
          return true;
        });
        const labelMap = {
          'nad': { medicationName: 'NAD+', brandNames: 'NAD+ Injections' },
          'glutathione': { medicationName: 'Glutathione', brandNames: 'Glutathione' },
          'metformin': { medicationName: 'Metformin', brandNames: 'Glucophage' },
          'mic+b12': { medicationName: 'MIC+B12', brandNames: 'Lipotropic Injections' },
          'mic-b12': { medicationName: 'MIC+B12', brandNames: 'Lipotropic Injections' },
          'b12': { medicationName: 'Vitamin B12', brandNames: 'Cobalamin' },
          'vitamin-b12': { medicationName: 'Vitamin B12', brandNames: 'Cobalamin' },
        };
        const meta = labelMap[matchToken] || { medicationName: 'Anti-Aging Support', brandNames: 'NAD+ / MIC+B12 / Glutathione / B12 / Metformin' };
        setActivePlans({ ...meta, plans: filteredPlans });
      } else if (isMetabolic) {
        // Match plans whose name OR slug references the patient's metabolic
        // medication. Falls back to all sermorelin+tesamorelin plans if the
        // patient's preference can't be parsed (defensive — backend should
        // always seed patientPreference on the original metabolic visit).
        const matchToken = metabolicMed.includes('tesamorelin')
          ? 'tesamorelin'
          : metabolicMed.includes('sermorelin')
            ? 'sermorelin'
            : null;
        const filteredPlans = (res.List || []).filter((plan) => {
          const slug = (plan.plan_slug || '').toLowerCase();
          const name = (plan.plan_name || '').toLowerCase();
          const med = (plan.plan_glp_medicine || '').toLowerCase();
          if (matchToken) {
            return slug.includes(matchToken) || name.includes(matchToken) || med.includes(matchToken);
          }
          return ['sermorelin', 'tesamorelin'].some((m) =>
            slug.includes(m) || name.includes(m) || med.includes(m)
          );
        });
        const labelMap = {
          tesamorelin: { medicationName: 'Tesamorelin', brandNames: 'Egrifta' },
          sermorelin: { medicationName: 'Sermorelin', brandNames: 'Geref' },
        };
        const meta = labelMap[matchToken] || { medicationName: 'Metabolic Support', brandNames: 'Sermorelin / Tesamorelin' };
        setActivePlans({ ...meta, plans: filteredPlans });
      } else if (isBrandedWeightloss) {
        // Branded GLP-1 follow-ups inherit the product from the parent visit
        // (FE continues the parent patientPreference; restart-at-starter is
        // resolved backend-side). Plans are FE-side hardcoded exports, same
        // pattern as WSH/MSH.
        const allPlans = flattenCategoryPlans(brandedWeightlossPlans);
        const prefName = (userData?.patientPreference?.[0]?.name || '').toLowerCase();
        const tokens = ['wegovy', 'ozempic', 'zepbound', 'mounjaro', 'foundayo'];
        const filteredPlans = prefName
          ? allPlans.filter((plan) => {
              const hay = `${plan.plan_slug} ${plan.plan_name} ${plan.plan_glp_medicine}`.toLowerCase();
              return tokens.some((tok) => prefName.includes(tok) && hay.includes(tok));
            })
          : [];
        setActivePlans({
          medicationName: 'Branded GLP-1 Weight Loss',
          brandNames: 'Wegovy / Ozempic / Zepbound / Mounjaro / Foundayo',
          plans: filteredPlans.length > 0 ? filteredPlans : allPlans,
        });
      } else if (isWomenSexualHealth || isMensSexualHealth) {
        // WSH/MSH follow-ups inherit the product from the parent visit — the FE
        // sends patientPreference:[] and the BE inherits, so this list is
        // informational. Show the patient's current product (derived from their
        // existing patientPreference); fall back to all plans for the goalType.
        // Plans are FE-side hardcoded exports (not in active-list-v2), same as
        // anti-aging.
        const allPlans = flattenCategoryPlans(
          isMensSexualHealth ? mensSexualHealthPlans : womenSexualHealthPlans,
        );
        const prefName = (userData?.patientPreference?.[0]?.name || '').toLowerCase();
        const tokens = ['oxytocin', 'pt-141', 'pt141', 'pde5', 'sildenafil', 'tadalafil', 'apomorphine'];
        const filteredPlans = prefName
          ? allPlans.filter((plan) => {
              const hay = `${plan.plan_slug} ${plan.plan_name} ${plan.plan_glp_medicine}`.toLowerCase();
              return tokens.some(
                (tok) => prefName.includes(tok) && hay.includes(tok.replace('-', '')),
              );
            })
          : [];
        setActivePlans({
          medicationName: isMensSexualHealth ? "Men's Sexual Health" : "Women's Sexual Health",
          brandNames: isMensSexualHealth
            ? 'PT-141 / Oxytocin / PDE5i / Apomorphine'
            : 'PT-141 / Oxytocin',
          plans: filteredPlans.length > 0 ? filteredPlans : allPlans,
        });
      } else if(selectedMedication==='tirzepatide'){
        const filteredPlans = res.List.filter(plan => plan.plan_glp_medicine.toLowerCase().includes('tirzepatide'));
       setActivePlans({medicationName: 'Tirzepatide',
        brandNames: 'Zepbound, Mounjaro',plans:filteredPlans});
      }
      else if(selectedMedication==='semaglutide'){
        const filteredPlans = res.List.filter(plan => plan.plan_glp_medicine.toLowerCase().includes('semaglutide'));
        setActivePlans({medicationName: 'Semaglutide',
        brandNames: 'Ozempic, Wegovy',plans:filteredPlans});
        console.log('Filtered Plans:', filteredPlans);
      }
     }).catch((err)=>{
        setLoading(false)
      console.error('Error fetching active plans:', err);
     })
    // setLoading + parent-stable values intentionally excluded — this effect
    // fires once on mount to fetch the plan list. The branch keys
    // (isMetabolic / metabolicMed / selectedMedication) are derived from
    // props that are stable across the checkout step's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])
  






  
  const [selectedPlan, setSelectedPlan] = useState([]);
  const [cardNumber, setCardNumber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvv, setCvv] = useState('');
  const [nameOnCard, setNameOnCard] = useState('');
  const [errors, setErrors] = useState({});
  const [currentPlan,SetcurrentPlan ]=useState({})


  useEffect(() => {
    if (activePlans.plans && activePlans.plans.length > 0) {
      setSelectedPlan(activePlans.plans[0].plan_slug);
    }
  }, [activePlans]);

  useEffect(()=>{
    if (activePlans.plans && activePlans.plans.length > 0) {
      const plan = activePlans.plans.find(p => p.plan_slug === selectedPlan);
      if (plan) {
        SetcurrentPlan(plan);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[selectedPlan]);


  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    return parts.length ? parts.join(' ') : value;
  };

  const formatExpiry = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return v.substring(0, 2) + '/' + v.substring(2, 4);
    }
    return v;
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!nameOnCard.trim()) {
      newErrors.nameOnCard = 'Name on card is required';
    }
    if (!cardNumber.replace(/\s/g, '') || cardNumber.replace(/\s/g, '').length < 16) {
      newErrors.cardNumber = 'Valid card number is required';
    }
    if (!expiry || expiry.length < 5) {
      newErrors.expiry = 'Valid expiry date is required';
    }
    if (!cvv || cvv.length < 3) {
      newErrors.cvv = 'Valid CVV is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Plans not loaded yet — keep the original loading state (the parent shows a
  // spinner via setLoading; render nothing structural until the list arrives).
  if (!activePlans.plans || activePlans.plans.length === 0) {
    return <div data-testid="checkout-step" className="max-w-4xl mx-auto" />;
  }

  // Derive the recommendation-slide product from the medication + the supply the
  // patient currently has selected (defaults to the first plan via the existing
  // selectedPlan effect). Pricing/name/sub mirror the pay-phase summary exactly.
  const defaultPlan =
    activePlans.plans.find((p) => p.plan_slug === selectedPlan) || activePlans.plans[0];
  const recommendationProduct = {
    name: activePlans.medicationName,
    sub: defaultPlan?.plan_name,
    price: `$${defaultPlan?.plan_price}`,
  };

  return (
    <RecommendationGate
      product={recommendationProduct}
      headline="Continue your treatment."
      subhead="Confirm the supply you'd like your provider to prescribe."
    >
    <div data-testid="checkout-step" className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-2xl md:text-3xl font-semibold text-gray-900 mb-2">
          Review treatment and pay
        </h2>
        <p className="text-gray-500">
          Select your preferred supply for {activePlans.medicationName}
        </p>
        <p className="text-sm text-gray-400 mt-1">
          ({activePlans.brandNames})
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Left Column - Plan Selection */}
        <div className="space-y-4">
          {/* Plan Cards */}

          {
            activePlans.plans && activePlans.plans.map((plan) => (
                 <div
            key={plan.plan_slug}
            onClick={() => setSelectedPlan(plan.plan_slug)}
            className={`relative cursor-pointer rounded-2xl border-2 p-5 transition-all ${
              selectedPlan === plan.plan_slug
                ? 'border-[#2d8a5f] bg-[#f0faf4]'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="font-semibold text-gray-900">{plan.plan_name}</h3>
                <p className="text-sm text-gray-500">{plan.plan_content.split(',')[0]}</p>
              </div>
              <div className="text-right">
                <span className="text-2xl font-bold text-gray-900">${plan.plan_price}</span>
              </div>
            </div>
            
            {/* Features */}
            <ul className="space-y-1 mb-3">
              {plan.plan_content.split(',').slice(1, 4).map((feature, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                  <svg className="w-4 h-4 text-[#2d8a5f] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  {feature}
                </li>
              ))}
            </ul>

            {/* Selection indicator */}
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
              selectedPlan === plan.plan_slug ? 'border-[#2d8a5f] bg-[#2d8a5f]' : 'border-gray-300'
            }`}>
              {selectedPlan === plan.plan_slug && (
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              )}
            </div>
          </div>
            ))}
     

       
      

          {/* Total Section */}
          <div className="bg-[#f0faf4] rounded-2xl p-5 mt-4">
            <div className="flex justify-between items-center">
              <span className="font-semibold text-lg">Your price</span>
              <span className="text-2xl font-bold text-[#2d8a5f]">${currentPlan.plan_price}</span>
            </div>
          </div>

          {/* What's Included */}
          <div className="bg-white border border-gray-200 rounded-2xl p-5">
            <h4 className="font-semibold text-gray-900 mb-3">What&apos;s included:</h4>
            <BenefitList
              items={[
                'Provider review & prescription included',
                'Free, discreet shipping',
                'Unlimited care-team messaging',
                'No long-term contract — cancel anytime',
              ]}
            />
          </div>
        </div>

        {/* Right Column - Payment Form */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <ProviderReviewNotice className="mb-4" />
          <TrustBanner>
            <span className="font-semibold">Free expedited shipping</span> — your treatment ships in 2–3 days once your provider prescribes it.
          </TrustBanner>
          {/* §patient-address: follow-up Ship-to card (FollowUpForm). While its
              edit form is open, payment is held so a charge can't go through
              with an unsaved address. */}
          {shipTo}
          <h3 className="font-semibold text-gray-900 mb-4">Payment details</h3>
          {paymentHeld ? (
            <p className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3" data-testid="payment-held">
              Save or cancel your shipping address change to continue to payment.
            </p>
          ) : stagingTest ? (
            <div className="space-y-3">
              <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
                ⚠️ Staging sign-off mode — Square is bypassed. This submits a
                test <strong>{followupVisitTypeLabel}</strong> visit to Beluga staging with no charge.
              </p>
              <button
                type="button"
                data-testid="staging-bypass-pay"
                disabled={isProcessing}
                onClick={() => onComplete({ paymentId: 'STAGING_TEST_BYPASS' })}
                className="w-full rounded-xl bg-[#2d8a5f] px-4 py-3 font-semibold text-white disabled:opacity-50"
              >
                {isProcessing
                  ? 'Submitting…'
                  : '[Staging test] Skip payment & submit followup to Beluga staging'}
              </button>
            </div>
          ) : (
            <SquarePaymentForm modalityVisitType={modalityVisitType} modalityState={modalityState} planId={currentPlan.plan_slug} amount={(Number(currentPlan.plan_price) || 0) * 100} onComplete={onComplete} answers={answers} setLoading={setLoading} clearSavedProgress={clearSavedProgress} primaryGoal={primaryGoal} currentStep={currentStep} timestamp={timestamp} email={email}/>
          )}
        </div>
      </div>
    </div>
    </RecommendationGate>
  );
};

export default CheckoutStep;